import React, { PureComponent, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import * as am5 from "@amcharts/amcharts5";
import * as am5xy from "@amcharts/amcharts5/xy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";
import am5locales_tr_TR from "@amcharts/amcharts5/locales/tr_TR";
import am5locales_en_US from "@amcharts/amcharts5/locales/en_US";
import { ThemeContext } from '../../../../context/theme-context';
import COLORS from '../../../../constants/color';
import moment from 'moment';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import ErrorWithReload from '../../../../components/charts/ErrorWithReload';

import '../Summary.scss';

const LineChart = ({ data, addData, securitytInfo, onReload }) => {
    const [root, setRoot] = useState(null);
    const chartRef = useRef(null);
    const containerRef = useRef(null);
    const [initialized, setInitialized] = useState(false);
    const [symbolCode, setSymbolCode] = useState(securitytInfo?.code);
    const [symbolName, setSymbolName] = useState(securitytInfo?.name);
    const [theme, setTheme] = useState('dark');
    const [hasError, setHasError] = useState(false);
    const currentTheme = useContext(ThemeContext).theme;

    // Get the language code from redux store
    const { i18n, t } = useTranslation();
    const profileBasicDetail = useSelector(state => state.profile.basicDetail);
    const languageCode = profileBasicDetail.languagecode || i18n.language || 'tr-TR';

    // Mevcut verilere yeni veri eklenmesini işleme


    useEffect(() => {
        // Tema değişikliğini izle
        setTheme(currentTheme);
    }, [currentTheme]);

    // Seçili hisse değiştiğinde sembol bilgisini güncelle
    useEffect(() => {
        if (securitytInfo?.code) setSymbolCode(securitytInfo.code);
        if (securitytInfo?.name) setSymbolName(securitytInfo.name);
    }, [securitytInfo?.code, securitytInfo?.name]);

    // tooltipHTML statik bir string; chart kurulurken bir kez üretiliyor.
    // Sembol/tema/dil değiştiğinde grafiği yeniden kurmadan seriye tekrar set et.
    useEffect(() => {
        const series = chartRef.current?.chart?.series.getIndex(0);
        if (!series) return;
        series.set("tooltipHTML", createTooltipHTML());
    }, [symbolCode, symbolName, theme, languageCode]);

    useEffect(() => {
        if (!data || !chartRef.current || !chartRef.current.chart) {
            return;
        }

        try {
            const formattedData = data.map(item => {
                if (typeof item.Date !== 'number') {
                    return { ...item, Date: new Date(item.Date).getTime() };
                }
                return item;
            });

            const series = chartRef.current.chart.series.getIndex(0);
            const volumeSeries = chartRef.current.chart.series.getIndex(1);

            if (!series || !volumeSeries) {
                setHasError(true);
                return;
            }

            series.data.setAll(formattedData);
            volumeSeries.data.setAll(formattedData);

            series.appear(1000);
            chartRef.current.chart.appear(1000, 100);

            setHasError(false);
        } catch (error) {
            setHasError(true);
        }
    }, [data, theme]);

    // addData props ile yeni veri geldiğinde bu veriyi mevcut veriye ekle
    // Yeni veri eklendiğinde mevcut verilere ekleyip güncelleme

    useEffect(() => {
        if (!addData || !chartRef.current || !chartRef.current.chart) {
            return;
        }

        try {
            const formattedAddData = { ...addData };
            if (typeof formattedAddData.Date !== 'number') {
                formattedAddData.Date = new Date(formattedAddData.Date).getTime();
            }

            const seriesList = [
                chartRef.current.chart.series.getIndex(0),
                chartRef.current.chart.series.getIndex(1)
            ];

            if (!seriesList[0] || !seriesList[1]) return;

            seriesList.forEach(series => {
                if (!series.data || !series.data.values) return;

                let currentData = [...series.data.values];
                currentData = currentData.filter(item =>
                    !moment(new Date(item.Date)).isSame(moment(new Date(formattedAddData.Date)), 'day')
                );
                series.data.setAll([...currentData, formattedAddData]);
            });
        } catch (error) {
            // silently ignore streaming update errors
        }
    }, [addData, theme]);

    useLayoutEffect(() => {
        setHasError(false);

        try {

            // Önceki chart'ı temizle
            if (chartRef.current && chartRef.current.root) {
                chartRef.current.root.dispose();
            }

            // Yeni chart oluştur
            if (!containerRef.current) return;
            let root = am5.Root.new(containerRef.current);
            root.setThemes([
                am5themes_Animated.new(root),
            ]);

            // Dynamically set locale based on language code
            if (languageCode === 'en-US') {
                root.locale = am5locales_en_US;
            } else {
                root.locale = am5locales_tr_TR;
            }

            var chart = root.container.children.push(
                am5xy.XYChart.new(root, {
                    panY: true,
                    wheelY: "none",
                    layout: root.verticalLayout,
                })
            );

            let cursor = chart.set("cursor", am5xy.XYCursor.new(root, {}));
            cursor.lineY.set("visible", false);

            // Create Start X-Axis
            let axisRendererX = am5xy.AxisRendererX.new(root, {
                skipEmptyPeriods: true,
                fill: theme === 'dark' ? am5.color('#626262') : am5.color('#231f20'),
            })

            axisRendererX.labels.template.setAll({
                fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231f20'),
            });

            let xAxis = chart.xAxes.push(
                am5xy.GaplessDateAxis.new(root, {
                    maxDeviation: 1,
                    groupData: false,
                    baseInterval: { timeUnit: "day", count: 1 }, // Use day as time unit
                    gridIntervals: [
                        { timeUnit: "day", count: 1 },
                        { timeUnit: "week", count: 1 },
                        { timeUnit: "month", count: 1 },
                        { timeUnit: "year", count: 1 }
                    ],
                    renderer: axisRendererX,
                    minGridDistance: 60, // Start with a reasonable minGridDistance
                })
            );

            xAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "wrap",
                maxWidth: 250,
                textAlign: "center",
                fontSize: 12,
                fontFamily: 'Roboto-Medium',
                fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
                tooltipDateFormat: "dd/MM/yyyy",
                opacity: 1,
                position: "right",
                opposite: false,
            });

            xAxis.get("renderer").grid.template.setAll({
                strokeOpacity: 0,
                stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
                strokeDasharray: [4, 4],
            });
            // Create End X-Axis

            // Create Start Y-Axis
            var yAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
                renderer: am5xy.AxisRendererY.new(root, {
                    pan: "zoom",
                    opposite: false,
                })
            }));
            yAxis.get("renderer").grid.template.setAll({
                strokeOpacity: 1,
                stroke: theme === 'dark' ? am5.color('#626262') : am5.color('#E4E4E4'),
                strokeDasharray: [4, 4],
                fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
                opposite: true,
            });

            yAxis.get("renderer").labels.template.setAll({
                oversizedBehavior: "wrap",
                maxWidth: 250,
                textAlign: "center",
                fontSize: 12,
                fontFamily: 'Roboto-Medium',
                fill: theme === 'dark' ? am5.color('#fff') : am5.color('#231F20'),
                opacity: 1,
            });

            // Create End Y-Axis

            var tooltipHTML = createTooltipHTML(theme);

            let tooltipLineSeries = am5.Tooltip.new(root, {
                keepTargetHover: true,
                getFillFromSprite: true,
                getStrokeFromSprite: true,
                autoTextColor: true,
                getLabelFillFromSprite: true,
                fill: am5.color("#333"),
            });

            tooltipLineSeries.get("background").setAll({
                fill: am5.color("#333"),
                fillOpacity: 0,
                strokeOpacity: 0,
                getStrokeFromSprite: true,
            });
            tooltipLineSeries.label.set("fill", am5.color("#231f20"));

            var series = chart.series.push(am5xy.LineSeries.new(root, {
                name: 'series',
                minBulletDistance: 30,
                xAxis: xAxis,
                yAxis: yAxis,
                valueYField: "Close",
                valueXField: "Date", // <-- fix here
                connect: true,
                stroke: theme === 'dark' ? am5.color('#35353e') : am5.color('#fff'),
                fill: theme === 'dark' ? am5.color('#35353e') : am5.color('#fff'),
                fillOpacity: 1,
                strokeWidth: 1,
                tooltip: tooltipLineSeries,
                tooltipHTML: tooltipHTML,
            }));

            series.fills.template.setAll({
                fillOpacity: 0.9,
                visible: true,
                fill: am5.color('#2FBC99'),
                fillGradient: am5.LinearGradient.new(root, {
                    stops: [{
                        color: am5.color('#2FBC99')
                    }, {
                        color: theme === 'dark' ? am5.color('#35353e') : am5.color('#fff'),
                    }]
                })
            });



            // y axis for volume
            var volumeAxisRenderer = am5xy.AxisRendererY.new(root, {});
            volumeAxisRenderer.grid.template.set("forceHidden", true);
            volumeAxisRenderer.labels.template.set("forceHidden", true);

            var volumeAxis = chart.yAxes.push(am5xy.ValueAxis.new(root, {
                height: am5.percent(25),
                y: am5.percent(100),
                centerY: am5.percent(100),
                panY: false,
                renderer: volumeAxisRenderer,
            }));

            var volumeSeries = chart.series.push(am5xy.ColumnSeries.new(root, {
                name: "Volume Series",
                xAxis: xAxis,
                yAxis: volumeAxis,
                valueYField: "Volume",
                valueXField: "Date", // <-- fix here
                tooltip: am5.Tooltip.new(root, {
                    labelText: (languageCode === 'en-US' ? 'Volume' : 'Hacim') + ": {valueY}"
                })
            }));

            volumeSeries.columns.template.setAll({ fillOpacity: 1, strokeOpacity: 0, width: am5.percent(30) })


            // chart.set("scrollbarX", am5.Scrollbar.new(root, {
            //     orientation: "horizontal",
            //     position: "bottom",

            // }));

            // chart.set("scrollbarX", am5.Scrollbar.new(root, {
            //     orientation: "horizontal"
            // }));

            // series.strokes.template.setAll({
            //     strokeWidth: 2,
            //     stroke: am5.color('#42A5F5'),
            //     fill: am5.color('#42A5F5'),
            // });
            // if (data != undefined) {
            //     data.forEach((item) => {
            //         item.Date = new Date(item.Date).getTime();
            //     });
            //     series.data.setAll(data);
            //     volumeSeries.data.setAll(data);
            // }
            // Kürsör ayarlarını yapın
            cursor.events.on("cursormoved", function (ev) {
                let dataItem = series.get("tooltip").dataItem;

                if (dataItem) {
                    let volumeDataItem = volumeSeries.dataItems.find(x => x.get("valueX") == dataItem.get("valueX"));
                    volumeSeries.get("tooltip").dataItem = volumeDataItem;
                    volumeSeries.get("tooltip").show();
                }
            });

            chart.set("scrollbarX", am5.Scrollbar.new(root, {
                orientation: "horizontal"
            }));

            series.appear(1000);
            chart.appear(1000, 100);
            chartRef.current = { chart, root };
            return () => {
                root.dispose();
            };
        } catch (error) {
            console.error("LineChart oluşturulurken hata:", error);
            setHasError(true); // Chart oluşturma hatası durumunda hata bayrağını set et
        }
    }, [theme, languageCode]); // Added languageCode as a dependency

    const createTooltipHTML = () => {
        // Get translated strings based on current language
        const dateLabel = languageCode === 'en-US' ? 'Date' : 'Tarih';
        const priceLabel = languageCode === 'en-US' ? 'Price' : 'Fiyat';
        const volumeLabel = languageCode === 'en-US' ? 'Volume' : 'Hacim';

        // Temaya göre renkleri ayarlayın
        const priceColor = theme === 'dark' ? '#2FBC99' : '#2FBC99';
        const volumeColor = theme === 'dark' ? '#67b7dc' : '#67b7dc';
        const backgroundColor = theme === 'dark' ? '#121212' : '#FFFFFF';
        const textColor = theme === 'dark' ? '#fff' : '#231f20';
        const textGray = theme === 'dark' ? '#FFFFFF' : '#757575';

        return `<div style="padding: 10px; background-color: ${backgroundColor}; color: ${textColor}; border-radius: 5px; min-width: 180px; border:.5px solid #b5b5b5">
        <div style="display:inline-block; text-align:right; float:right;"><strong>${symbolCode} <span style="color:${priceColor}"> - ${symbolName}</span></strong></div>
        
        <div style="display:block">
            <label style="text-align:right; float:right; padding-left:10px"> 
          <span style="color:${priceColor}; font-size:14px; font-family=Roboto-Medium;"><strong>${dateLabel}</strong></span> - <span style="color:${textGray}; font-size:14px; font-family=Roboto-Medium;">{Date.formatDate('dd/MM/yyyy')}</span>
            </label> 
        </div>

        <div style="margin-top: 5px; display:inline-block; margin:0;">
            <div>
                <span style="font-size:16px; font-family=Roboto-Medium; background-color: ${priceColor}; width: 10px; height: 10px; border-radius: 50%; display: inline-block;"></span>
               <span style="font-size:16px; font-family=Roboto-Medium;">${priceLabel}: </span><label style="display:block; text-align:right; float:right; margin:0; padding-right:0; font-size:16px; font-family=Roboto-Medium">{valueY} ₺</label>
            </div>
            <div>
                <span style="font-size:16px; font-family=Roboto-Medium; background-color: ${volumeColor}; width: 10px; height: 10px; border-radius: 50%; display: inline-block;"></span>
                <span style="font-size:16px; font-family=Roboto-Medium;">${volumeLabel}: </span><label style="text-align:right; float:right; padding-left:10px; font-size:16px; font-family=Roboto-Medium">{Volume}</label> 
            </div>
        </div>
        </div>`;
    }

    // Grafik verisi yükleme hatası olduğunda yeniden yükleme fonksiyonu
    const handleReload = () => {
        setHasError(false);
        if (onReload && typeof onReload === 'function') {
            onReload();
        } else {
            // Eğer özel yeniden yükleme fonksiyonu tanımlanmamışsa, 
            // varsayılan olarak grafiği yeniden oluşturmayı dene
            if (chartRef.current && chartRef.current.root) {
                chartRef.current.root.dispose();
                chartRef.current = null;
            }
            setInitialized(false);
        }
    };

    return (
        <div className='index-analysis-line-chart' style={{ position: 'relative', height: '440px' }}>
            <div ref={containerRef} style={{ width: '100%', height: '100%' }}></div>
            {hasError && (
                <div style={{ position: 'absolute', inset: 0 }}>
                    <ErrorWithReload
                        onReload={handleReload}
                        height="100%"
                        errorMessage={t('componentErrors.chartLoadFailed')}
                    />
                </div>
            )}
        </div>
    )
}




export default LineChart;
